import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { currencyFormatter, formatDateTime } from "../../../Helpers/Utils";
import usePaymentEvents from "../../../Helpers/usePaymentEvents";
import LiveBadge from "../../../Components/LiveBadge";
import useListParams from "../../../Helpers/useListParams";
import TestModeTag from "../../../Components/TestModeTag";

// Payments hub. Three views over the same live feed:
//   Payments       - the ledger (one row per Razorpay order / payment), with
//                    money totals for the current filters
//   Payment links  - links sent to customers (created -> paid / expired ...)
//   Refunds        - every refund issued, across all bookings
//   Approvals      - refunds above the site's approval limit, waiting for a
//                    DIFFERENT admin to approve or reject them
// Rows refetch when the backend emits "payment:updated"; rows whose booking
// just changed flash briefly so a moving payment is easy to spot.

const LIMIT = 12;

const TABS = {
    payments: {
        label: "Payments",
        url: (page) => `/admin/payment/list/${page}`,
        statuses: ["created", "captured", "failed", "expired", "partially_refunded", "refunded"],
    },
    links: {
        label: "Payment links",
        url: (page) => `/admin/payment/link/list/${page}`,
        statuses: ["created", "paid", "failed", "expired", "cancelled"],
    },
    refunds: {
        label: "Refunds",
        url: (page) => `/admin/payment/refund/list/${page}`,
        statuses: [],
    },
    approvals: {
        label: "Approvals",
        url: (page) => `/admin/payment/refund/approval/list/${page}`,
        statuses: ["pending", "approved", "rejected", "failed"],
    },
};

const STATUS_BADGE = {
    created: "bg-secondary",
    captured: "bg-success",
    paid: "bg-success",
    failed: "bg-danger",
    expired: "bg-dark",
    cancelled: "bg-dark",
    partially_refunded: "bg-info text-dark",
    refunded: "bg-warning text-dark",
    pending: "bg-warning text-dark",
    processed: "bg-success",
    approved: "bg-success",
    rejected: "bg-dark",
};

const PAYMENT_FOR = ["Booking", "Extension", "Settlement"];

// A payment that was not applied (PaymentUtils.reviewUnappliedPayment): paid twice, or paid
// after it was no longer needed. Its refund waits in the Approvals tab.
const FLAG_LABEL = { duplicate: "Duplicate", late: "Late payment" };
const FLAG_OUTCOME = { pending: "refund awaiting approval", refunded: "refunded", kept: "refund rejected, kept", failed: "refund failed" };
const CHANNELS = { sdk: "Checkout (SDK)", link: "Payment link" };
const TAB_CHANNEL_LABEL = (row) => CHANNELS[row.channel] || "-";

const LIST_FILTERS = { status: "", paymentFor: "", channel: "", search: "", dateFrom: "", dateTo: "", paymentMode: "" };

const labelOf = (status) => (status || "-").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const PaymentList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, tab + filters in the query string - see Helpers/useListParams.
    const defaultTab = "payments";
    const { page, setPage, filters, setFilters, setFilter } = useListParams({ ...LIST_FILTERS, tab: defaultTab }, { zeroBased: true });
    const tab = (TABS[filters.tab] ? filters.tab : "payments");
    const { status, paymentFor, channel, search, dateFrom, dateTo, paymentMode } = filters;
    const setStatus = (value) => setFilter("status", value);
    const setPaymentFor = (value) => setFilter("paymentFor", value);
    const setChannel = (value) => setFilter("channel", value);
    const setPaymentMode = (value) => setFilter("paymentMode", value);
    const setSearch = (value) => setFilter("search", value);
    const setDateFrom = (value) => setFilter("dateFrom", value);
    const setDateTo = (value) => setFilter("dateTo", value);
    const [list, setList] = useState([]);
    const [counts, setCounts] = useState({});
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [decision, setDecision] = useState(null); // { row, decision }
    const [remarks, setRemarks] = useState("");
    const [deciding, setDeciding] = useState(false);
    const [flash, setFlash] = useState({});
    const searchDebounce = useRef(null);
    const lastSearch = useRef(search);
    const pageRef = useRef(0);
    pageRef.current = page;

    const requestFor = useCallback((pageNo, limit) => Handler({
        method: "post",
        url: TABS[tab].url(pageNo),
        params: { limit },
        data: {
            sortBy: "createdAt",
            sort: true,
            ...(status ? { status } : {}),
            ...(["payments", "links"].includes(tab) && paymentFor ? { paymentFor } : {}),
            ...(tab === "payments" && channel ? { channel } : {}),
            // Test mode: "live" / "test" narrows the list (the approvals list has no mode filter).
            ...(tab !== "approvals" && paymentMode ? { paymentMode } : {}),
            ...(search.trim() ? { search: search.trim() } : {}),
            ...(dateFrom ? { createdFrom: new Date(dateFrom).toISOString() } : {}),
            ...(dateTo ? { createdTo: new Date(`${dateTo}T23:59:59`).toISOString() } : {}),
        },
    }), [tab, status, paymentFor, channel, search, dateFrom, dateTo, paymentMode]);

    const load = useCallback(async (pageNo, { quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        const response = await requestFor(pageNo, LIMIT);
        if (response?.success) {
            const data = response?.data?.data || {};
            setList(data.list || []);
            setCounts(data.counts || {});
            setSummary(data.summary || null);
            setHasNext(!!data.next);
        } else if (!quiet) {
            setList([]);
            setCounts({});
            setSummary(null);
            setHasNext(false);
        }
        if (!quiet) setLoading(false);
    }, [requestFor]);

    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        // Wait for typing to pause before searching; page / tab / filter changes load straight away.
        const delay = search !== lastSearch.current ? 350 : 0;
        lastSearch.current = search;
        searchDebounce.current = setTimeout(() => load(page), delay);
        return () => clearTimeout(searchDebounce.current);
    }, [load, search, page]);

    // Refetch the page the admin is looking at without a spinner; highlight
    // the booking whose payment moved.
    const eventTimer = useRef(null);
    const { connected } = usePaymentEvents({
        onEvent: (event) => {
            if (event.bookingId) {
                setFlash((prev) => ({ ...prev, [event.bookingId]: Date.now() }));
                setTimeout(() => setFlash((prev) => {
                    const next = { ...prev };
                    delete next[event.bookingId];
                    return next;
                }), 4000);
            }
            // several events usually arrive together (payment + link + booking)
            if (eventTimer.current) clearTimeout(eventTimer.current);
            eventTimer.current = setTimeout(() => load(pageRef.current, { quiet: true }), 400);
        },
        onResync: () => load(pageRef.current, { quiet: true }),
    });
    useEffect(() => () => eventTimer.current && clearTimeout(eventTimer.current), []);

    const switchTab = (key) => {
        if (key === tab) return;
        setFilters({ ...LIST_FILTERS, tab: key });
        setPage(0);
        setList([]);
        setCounts({});
        setSummary(null);
    };

    // Pulls up to 500 rows for the current filters and saves them as a CSV.
    const exportCsv = async () => {
        setExporting(true);
        const response = await requestFor(0, 500);
        setExporting(false);
        const rows = response?.data?.data?.list || [];
        if (!rows.length) return;
        const cols = isApprovals
            ? [["Approval", "approvalId"], ["Booking", "bookingId"], ["Customer", "customerName"], ["Order", "orderId"], ["Category", "category"], ["Reason", "reason"], ["Mode", "mode"], ["Status", "status"], ["Amount", "amount"], ["Created", "createdAt"]]
            : isRefunds
            ? [["Refund", "refundId"], ["Booking", "bookingId"], ["Customer", "customerName"], ["Payment", "paymentId"], ["Order", "orderId"], ["Category", "category"], ["Mode", "mode"], ["Status", "status"], ["Amount", "amount"], ["Created", "createdAt"]]
            : isLinks
                ? [["Link", "linkId"], ["Booking", "bookingId"], ["Customer", "customerName"], ["For", "paymentFor"], ["Mode", "mode"], ["Status", "status"], ["Amount", "totalAmount"], ["Created", "createdAt"]]
                : [["Order", "orderId"], ["Payment", "paymentId"], ["Booking", "bookingId"], ["Customer", "customerName"], ["For", "paymentFor"], ["Channel", "channel"], ["Mode", "mode"], ["Status", "status"], ["Amount", "totalAmount"], ["Refunded", "refundedAmount"], ["Created", "createdAt"]];
        const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const csv = [cols.map(([h]) => cell(h)).join(","), ...rows.map((r) => cols.map(([, k]) => cell(r[k])).join(","))].join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const submitDecision = async () => {
        if (!decision) return;
        setDeciding(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/payment/refund/approval/${decision.row.approvalId}`,
            data: { decision: decision.decision, ...(remarks.trim() ? { remarks: remarks.trim() } : {}) },
        });
        setDeciding(false);
        if (response?.success) {
            toast.success(decision.decision === "approved" ? "Refund approved and sent" : "Refund request rejected");
            setDecision(null);
            setRemarks("");
            load(pageRef.current, { quiet: true });
        } else {
            toast.error(response?.data?.message || "Could not record this decision");
        }
    };

    const totalCount = Object.values(counts).reduce((sum, n) => sum + n, 0);
    const isLinks = tab === "links";
    const isRefunds = tab === "refunds";
    const isApprovals = tab === "approvals";
    const statusChips = [...new Set([...TABS[tab].statuses, ...Object.keys(counts)])];
    const colCount = isApprovals ? 8 : isRefunds ? 8 : isLinks ? 7 : 9;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                    <h5 className="fw-semibold mb-0">Payments</h5>
                    <LiveBadge connected={connected} />
                </div>
                <div className="btn-group">
                    {Object.entries(TABS).map(([key, def]) => (
                        <button key={key} type="button" className={`btn btn-sm ${tab === key ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => switchTab(key)}>
                            {def.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Money totals for the current filters */}
            {tab === "payments" && summary && (
                <div className="row g-2 mb-3">
                    {[
                        ["Collected", summary.collected, "text-success"],
                        ["Refunded", summary.refunded, "text-warning"],
                        ["Net received", summary.net, "text-dark"],
                        ["Awaiting payment", summary.open, "text-secondary"],
                    ].map(([label, value, tone]) => (
                        <div className="col-6 col-md-3" key={label}>
                            <div className="card border-0 shadow-sm rounded-4 p-3 h-100">
                                <div className="text-muted small">{label}</div>
                                <div className={`fs-5 fw-semibold ${tone}`}>{currencyFormatter(value || 0)}</div>
                            </div>
                        </div>
                    ))}
                    <div className="col-12 text-muted small">
                        Collected by type: {Object.entries(summary.byFor || {}).map(([k, v]) => `${k} ${currencyFormatter(v)}`).join(" | ") || "none"}
                        {" . "}By channel: {Object.entries(summary.byChannel || {}).map(([k, v]) => `${CHANNELS[k] || "Not recorded"} ${currencyFormatter(v)}`).join(" | ") || "none"}
                    </div>
                    <div className="col-12 small">
                        {summary.scope === "test" ? (
                            <span><span className="badge bg-warning text-dark me-1">TEST</span>Test payments only: Razorpay test account, no real money.</span>
                        ) : (
                            <span className="text-muted">These totals leave out test payments. Pick "Test only" under Mode to see them.</span>
                        )}
                    </div>
                </div>
            )}

            {/* Status strip */}
            <div className="d-flex flex-wrap gap-2 mb-3">
                <button type="button" className={`btn btn-sm ${status === "" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setStatus("")}>
                    All <span className="badge bg-light text-dark ms-1">{totalCount}</span>
                </button>
                {statusChips.map((s) => (
                    <button key={s} type="button" className={`btn btn-sm ${status === s ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setStatus(status === s ? "" : s)}>
                        {labelOf(s)} <span className="badge bg-light text-dark ms-1">{counts[s] ?? 0}</span>
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={isLinks ? "Booking ID, link ID, customer name, phone or email" : isApprovals ? "Approval ID, booking ID or order ID" : isRefunds ? "Refund ID, booking ID, order ID or payment ID" : "Booking ID, order ID, payment ID or link ID"}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    {(tab === "payments" || isLinks) && (
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Payment for</label>
                            <select className="form-select" value={paymentFor} onChange={(e) => setPaymentFor(e.target.value)}>
                                <option value="">All</option>
                                {PAYMENT_FOR.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    )}
                    {tab === "payments" && (
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Channel</label>
                            <select className="form-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
                                <option value="">All</option>
                                {Object.entries(CHANNELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                    )}
                    {!isApprovals && (
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Mode</label>
                            <select className="form-select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                <option value="">Live and test</option>
                                <option value="live">Live only</option>
                                <option value="test">Test only</option>
                            </select>
                        </div>
                    )}
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Created from</label>
                        <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Created to</label>
                        <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="col-md-2 d-flex gap-2">
                        <button className="btn btn-outline-secondary w-100" onClick={() => { setFilters({ ...LIST_FILTERS, tab }); setPage(0); }}>Reset</button>
                        <button className="btn btn-outline-dark w-100" onClick={exportCsv} disabled={exporting || loading}>{exporting ? "..." : "CSV"}</button>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="card border-0 shadow-sm rounded-4">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>{isApprovals ? "Approval" : isRefunds ? "Refund" : isLinks ? "Link" : "Order"}</th>
                                <th>Booking</th>
                                <th>Customer</th>
                                <th>{isRefunds || isApprovals ? "Category" : "For"}</th>
                                {tab === "payments" && <th>Channel</th>}
                                <th>Status</th>
                                <th className="text-end">Amount</th>
                                {tab === "payments" && <th className="text-end">Refunded</th>}
                                <th>{isApprovals ? "Requested" : "Created"}</th>
                                {isApprovals && <th className="text-end">Decision</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={colCount} className="text-center text-muted py-4">Loading...</td></tr>
                            )}
                            {!loading && list.length === 0 && (
                                <tr><td colSpan={colCount} className="text-center text-muted py-4">Nothing matches.</td></tr>
                            )}
                            {!loading && list.map((row) => {
                                const key = isApprovals ? row.approvalId : isRefunds ? row.refundId : isLinks ? row.linkId : row.orderId;
                                const amount = isRefunds || isApprovals ? row.amount : row.totalAmount;
                                return (
                                    <tr
                                        key={key}
                                        role="button"
                                        style={{ cursor: "pointer", transition: "background-color .6s", backgroundColor: flash[row.bookingId] ? "#fff8dc" : undefined }}
                                        onClick={() => row.bookingId && navigate(`/booking/details/${row.bookingId}`)}
                                    >
                                        <td>
                                            <div className="fw-semibold">{key}<TestModeTag mode={row.mode} /></div>
                                            {tab === "payments" && row.paymentId && <div className="text-muted small">{row.paymentId}</div>}
                                            {isApprovals && (row.reason || row.orderId) && <div className="text-muted small">{row.reason || row.orderId}</div>}
                                            {isRefunds && (row.paymentId || row.orderId) && <div className="text-muted small">{row.paymentId || row.orderId}</div>}
                                            {tab === "payments" && row.sourceOrderId && <div className="text-muted small">Link payment, replaces {row.sourceOrderId}</div>}
                                            {tab === "payments" && row.paidOrderId && <div className="text-muted small">Paid as {row.paidOrderId}</div>}
                                            {tab === "payments" && row.duplicate?.reason && (
                                                <div className="text-danger small">
                                                    {row.duplicate.of ? `Already paid by ${row.duplicate.of}` : "Not applied to the booking"}
                                                    {FLAG_OUTCOME[row.duplicate.status] ? ` - ${FLAG_OUTCOME[row.duplicate.status]}` : ""}
                                                </div>
                                            )}
                                            {isApprovals && row.requestedBy === "system" && <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle mt-1">Auto-flagged {row.category === "duplicate_charge" ? "duplicate" : "late payment"}</span>}
                                        </td>
                                        <td>
                                            <div>{row.bookingId || "-"}</div>
                                            {row.bookingStage && <div className="text-muted small">{row.bookingStage}</div>}
                                        </td>
                                        <td>
                                            <div>{row.customerName || row.name || "-"}</div>
                                            {(row.customerPhone || row.contact) && <div className="text-muted small">{row.customerPhone || row.contact}</div>}
                                        </td>
                                        <td>{isRefunds || isApprovals ? (row.category || "-") : (row.paymentFor || "-")}</td>
                                        {tab === "payments" && <td className="small">{TAB_CHANNEL_LABEL(row)}</td>}
                                        <td>
                                            <span className={`badge ${STATUS_BADGE[row.status] || "bg-secondary"}`}>{labelOf(row.status)}</span>
                                            {tab === "payments" && row.duplicate?.reason && <span className="badge bg-danger ms-1">{FLAG_LABEL[row.duplicate.reason] || "Flagged"}</span>}
                                        </td>
                                        <td className="text-end">{currencyFormatter(amount || 0)}</td>
                                        {tab === "payments" && <td className="text-end">{row.refundedAmount ? currencyFormatter(row.refundedAmount) : "-"}</td>}
                                        <td className="small">{row.createdAt ? formatDateTime(row.createdAt) : "-"}</td>
                                        {isApprovals && (
                                            <td className="text-end text-nowrap" onClick={(e) => e.stopPropagation()}>
                                                {row.status === "pending" ? (
                                                    <>
                                                        <button type="button" className="btn btn-sm btn-success me-1" onClick={() => { setDecision({ row, decision: "approved" }); setRemarks(""); }}>Approve</button>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => { setDecision({ row, decision: "rejected" }); setRemarks(""); }}>Reject</button>
                                                    </>
                                                ) : (
                                                    <span className="text-muted small">{row.remarks || (row.reviewedAt ? formatDateTime(row.reviewedAt) : "-")}</span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="d-flex justify-content-between p-3">
                    <button className="btn btn-outline-secondary btn-sm" disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button>
                    <span className="text-muted small">Page {page + 1}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={!hasNext || loading} onClick={() => setPage(page + 1)}>Next</button>
                </div>
            </div>

            {decision && (
                <>
                    <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{decision.decision === "approved" ? "Approve refund" : "Reject refund"}</h5>
                                    <button type="button" className="btn-close" onClick={() => setDecision(null)} disabled={deciding}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-2">
                                        {decision.decision === "approved" ? "Send" : "Decline"} <strong>{currencyFormatter(decision.row.amount)}</strong> for booking {decision.row.bookingId || "-"} ({decision.row.category || "other"}).
                                    </p>
                                    {decision.row.reason && <p className="text-muted small">{decision.row.requestedBy === "system" ? "Flagged automatically: " : "Reason given: "}{decision.row.reason}</p>}
                                    {decision.row.requestedBy === "system" && decision.decision === "rejected" && <p className="text-muted small">Rejecting keeps this payment. The booking does not use it.</p>}
                                    {decision.decision === "approved" && <p className="text-muted small">Approving sends the money back to the customer straight away. You cannot approve a refund you raised yourself.</p>}
                                    <label className="form-label small mt-2">Remarks (optional)</label>
                                    <textarea className="form-control" rows={2} maxLength={300} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={deciding} />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setDecision(null)} disabled={deciding}>Cancel</button>
                                    <button type="button" className={`btn ${decision.decision === "approved" ? "btn-success" : "btn-danger"}`} onClick={submitDecision} disabled={deciding}>
                                        {deciding ? "Saving..." : decision.decision === "approved" ? "Approve and refund" : "Reject"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default PaymentList;
